import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faCheckCircle,
  faExclamationTriangle,
  faTimes,
} from '@fortawesome/pro-solid-svg-icons';

import { Button } from 'components/ui/button';

export default function ProcessingToast({
  batchStatus,
  batchError,
  fileCount = 0,
  hasConflicts = false,
  onReviewReady,
  onDismiss,
}) {
  const isUploading = batchStatus === 'uploading';
  const isProcessing = batchStatus === 'processing';
  const isActive = isUploading || isProcessing;
  const isDone = batchStatus === 'complete';
  const isError = batchStatus === 'error';

  // Auto-hide after 5s during active phases; reappear when status flips to complete/error.
  const [autoHidden, setAutoHidden] = useState(false);
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    if (!isActive) {
      setAutoHidden(false);
      setProgress(0);
      return undefined;
    }
    setAutoHidden(false);
    setProgress(0);
    // Defer next frame so the 0% width paints before transitioning to 100%
    const startProgress = setTimeout(() => setProgress(100), 20);
    const hideTimer = setTimeout(() => setAutoHidden(true), 5000);
    return () => {
      clearTimeout(startProgress);
      clearTimeout(hideTimer);
    };
  }, [batchStatus, isActive]);

  if (!isActive && !isDone && !isError) return null;
  if (isActive && autoHidden) return null;

  const borderColor = isError
    ? '#dc3545'
    : isDone && hasConflicts
      ? '#f59e0b'
      : '#28a745';

  const renderContent = () => {
    if (isUploading) {
      return (
        <div className="d-flex align-items-start gap-3">
          <FontAwesomeIcon className="flex-shrink-0 mt-1" icon={faSpinner} spin style={{ color: '#28a745' }} />
          <div>
            <p className="mb-0 fw-semibold small">
              {fileCount > 0 ? `Uploading ${fileCount} file${fileCount === 1 ? '' : 's'}…` : 'Uploading files…'}
            </p>
          </div>
        </div>
      );
    }

    if (isProcessing) {
      return (
        <div className="d-flex align-items-start gap-3">
          <FontAwesomeIcon className="flex-shrink-0 mt-1" icon={faCheckCircle} style={{ color: '#28a745' }} />
          <div>
            <p className="mb-1 fw-semibold small">
              {fileCount > 0 ? `${fileCount} files uploaded` : 'Files uploaded'} - extracting details
            </p>
            <p className="mb-0 text-muted small">
              This may take a few minutes. We&apos;ll let you know when we&apos;re done.
            </p>
          </div>
        </div>
      );
    }

    if (isDone && hasConflicts) {
      return (
        <>
          <div className="d-flex align-items-start gap-3 mb-3">
            <FontAwesomeIcon className="flex-shrink-0 mt-1" icon={faExclamationTriangle} style={{ color: '#f59e0b' }} />
            <div>
              <p className="mb-1 fw-semibold small">Document processing finished with issues</p>
              <p className="mb-0 text-muted small">Some fields need your review</p>
            </div>
          </div>
          <div className="d-flex justify-content-end">
            <Button label="Review" size="sm" variant="primary" onClick={onReviewReady} />
          </div>
        </>
      );
    }

    if (isDone && !hasConflicts) {
      return (
        <>
          <div className="d-flex align-items-start gap-3 mb-3">
            <FontAwesomeIcon className="flex-shrink-0 mt-1" icon={faCheckCircle} style={{ color: '#28a745' }} />
            <div>
              <p className="mb-0 fw-semibold small">Your documents have been processed</p>
            </div>
          </div>
          <div className="d-flex justify-content-end">
            <Button label="Confirm details" size="sm" variant="primary" onClick={onReviewReady} />
          </div>
        </>
      );
    }

    if (isError) {
      return (
        <>
          <div className="d-flex justify-content-between align-items-start mb-2">
            <div className="d-flex align-items-start gap-2">
              <FontAwesomeIcon
                className="flex-shrink-0 mt-1"
                icon={faExclamationTriangle}
                style={{ color: '#dc3545' }}
              />
              <p className="mb-0 small">{batchError || 'An error occurred while processing your documents.'}</p>
            </div>
            <button
              aria-label="Dismiss"
              className="btn btn-link btn-sm text-muted p-0 ms-2 flex-shrink-0"
              type="button"
              onClick={onDismiss}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </>
      );
    }

    return null;
  };

  return (
    <div
      aria-live="polite"
      role="status"
      style={{
        bottom: '1.5rem',
        left: '50%',
        maxWidth: '22rem',
        position: 'fixed',
        transform: 'translateX(-50%)',
        zIndex: 1080,
      }}
    >
      <div
        className="card shadow-lg"
        style={{ border: `2px solid ${borderColor}`, borderRadius: '0.5rem', overflow: 'hidden' }}
      >
        <div className="card-body p-3">
          {renderContent()}
        </div>
        {isActive && (
          <div
            style={{
              background: borderColor,
              height: '3px',
              transition: 'width 5s linear',
              width: `${progress}%`,
            }}
          />
        )}
      </div>
    </div>
  );
}

ProcessingToast.propTypes = {
  batchError: PropTypes.string,
  batchStatus: PropTypes.oneOf(['uploading', 'processing', 'complete', 'error']).isRequired,
  fileCount: PropTypes.number,
  hasConflicts: PropTypes.bool,
  onDismiss: PropTypes.func.isRequired,
  onReviewReady: PropTypes.func.isRequired,
};
